import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Plus, Loader2, Users2, ShieldAlert, Copy, Check, Search, Download,
  Settings2, Power, X, UserCog, KeyRound, Save,
} from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import GeneratedCredential from "../components/GeneratedCredential";
import { PLAN_LABELS } from "../lib/plans";
import { MX_STATES, MX_TOP_MUNICIPALITIES } from "../lib/mxStates";
import { createAdmin, setAdminActive, regenerateAdminPassword } from "../lib/adminActions";

const qrUrl = (link, size = 300) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(link)}`;

// Estado + municipio (con sugerencias por estado, pero texto libre para
// cualquier municipio que no esté en la lista corta) + pin de Google Maps —
// compartido entre el alta de agencia y su panel de edición.
function LocationFields({ form, setForm, idPrefix }) {
  const cityOptions = MX_TOP_MUNICIPALITIES[form.state] || [];
  return (
    <>
      <select
        value={form.state}
        onChange={(e) => setForm({ ...form, state: e.target.value, municipality: "" })}
        data-testid={`${idPrefix}-state`}
        className="bg-black border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
      >
        <option value="">Estado (opcional)</option>
        {MX_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <div>
        <input
          value={form.municipality}
          onChange={(e) => setForm({ ...form, municipality: e.target.value })}
          placeholder="Municipio / ciudad"
          list={`${idPrefix}-municipios`}
          data-testid={`${idPrefix}-municipality`}
          className="w-full bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
        />
        {cityOptions.length > 0 && (
          <datalist id={`${idPrefix}-municipios`}>
            {cityOptions.map((c) => <option key={c} value={c} />)}
          </datalist>
        )}
      </div>
      <input
        value={form.maps_pin}
        onChange={(e) => setForm({ ...form, maps_pin: e.target.value })}
        placeholder="Pin de Google Maps (link para compartir ubicación, opcional)"
        data-testid={`${idPrefix}-maps-pin`}
        className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2"
      />
    </>
  );
}

function AgencyEditPanel({ agency, admins, onClose, onSaved, onAdminsChanged }) {
  // El panel se renderiza arriba de la lista de tarjetas — al tocar "Editar"
  // en una tarjeta más abajo (sobre todo en celular), abría fuera de la
  // vista y parecía que no había pasado nada.
  const panelRef = useRef(null);
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const [form, setForm] = useState({
    agency_name: agency.agency_name,
    tagline: agency.tagline,
    primary_color: agency.primary_color,
    secondary_color: agency.secondary_color,
    contact_email: agency.contact_email,
    contact_phone: agency.contact_phone,
    whatsapp: agency.whatsapp,
    website: agency.website,
    state: agency.state || "",
    municipality: agency.municipality || "",
    maps_pin: agency.maps_pin || "",
    plan: agency.plan || "starter",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [togglingActive, setTogglingActive] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ booking_id: "", last_name: "", traveler_name: "", email: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminErr, setAdminErr] = useState("");
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [resettingId, setResettingId] = useState("");
  const [usage, setUsage] = useState(null);
  // El slug va en las URLs públicas (link/QR) — va aparte de `form`/`save()`
  // a propósito: cambiarlo es más delicado que el resto de los campos
  // (rompería links ya compartidos si no se preservara un alias), así que
  // tiene su propia confirmación y su propio endpoint.
  const [slugInput, setSlugInput] = useState(agency.slug);
  const [savingSlug, setSavingSlug] = useState(false);
  const [slugErr, setSlugErr] = useState("");
  const [slugSaved, setSlugSaved] = useState(false);

  const agencyAdmins = admins.filter((a) => a.agency_id === agency.id);

  useEffect(() => {
    api.get(`/agencies/${agency.slug}/usage`).then(({ data }) => setUsage(data)).catch(() => setUsage(null));
  }, [agency.slug]);

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      await api.patch(`/master/agencies/${agency.id}`, form);
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const renameSlug = async () => {
    const newSlug = slugInput.trim().toLowerCase();
    if (newSlug === agency.slug) return;
    const ok = window.confirm(
      `¿Cambiar el slug de "${agency.slug}" a "${newSlug}"?\n\nEl link/QR viejo (/a/${agency.slug}) sigue funcionando — pero conviene reimprimir el QR con el nuevo para que no queden dos circulando.`
    );
    if (!ok) return;
    setSlugErr("");
    setSavingSlug(true);
    try {
      await api.patch(`/master/agencies/${agency.id}/slug`, { new_slug: newSlug });
      setSlugSaved(true);
      onSaved();
      setTimeout(() => setSlugSaved(false), 2000);
    } catch (e) {
      setSlugErr(e?.response?.data?.detail || "No se pudo cambiar el slug");
    } finally {
      setSavingSlug(false);
    }
  };

  const toggleActive = async () => {
    const turningOff = agency.active;
    if (turningOff) {
      const ok = window.confirm(
        "¿Desactivar esta agencia?\n\nSus viajeros con viaje en curso o que empieza en menos de 3 días siguen entrando normal. Los demás viajeros ya no podrán iniciar sesión.\n\nLa agencia sigue pudiendo entrar a su cuenta, pero no puede editar su marca, subir PDFs, ni dar de alta administradores — solo responder chats de los viajeros que sí quedaron activos."
      );
      if (!ok) return;
    }
    setTogglingActive(true);
    try {
      await api.patch(`/master/agencies/${agency.id}`, { active: !agency.active });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo cambiar el estado");
    } finally {
      setTogglingActive(false);
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    setAdminErr("");
    setAddingAdmin(true);
    try {
      const data = await createAdmin({ ...newAdmin, agency_id: agency.id });
      setRevealedPasswords((m) => ({ ...m, [data.booking_id]: data.password }));
      setNewAdmin({ booking_id: "", last_name: "", traveler_name: "", email: "" });
      onAdminsChanged();
    } catch (e) {
      setAdminErr(e?.response?.data?.detail || "No se pudo crear el administrador");
    } finally {
      setAddingAdmin(false);
    }
  };

  const toggleAdmin = async (a) => {
    try {
      await setAdminActive(a.booking_id, !a.active);
      onAdminsChanged();
    } catch (e) {
      setAdminErr(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const resetAdminPassword = async (a) => {
    setResettingId(a.booking_id);
    setAdminErr("");
    try {
      const data = await regenerateAdminPassword(a.booking_id);
      setRevealedPasswords((m) => ({ ...m, [a.booking_id]: data.password }));
    } catch (e) {
      setAdminErr(e?.response?.data?.detail || "No se pudo generar la contraseña");
    } finally {
      setResettingId("");
    }
  };

  return (
    <div ref={panelRef} className="glass rounded-3xl p-7 border border-[var(--brand-cyan,#FF85AC)]/30 space-y-8" data-testid="agency-edit-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Settings2 className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
          <h2 className="font-display text-xl">Editando: {agency.agency_name}</h2>
        </div>
        <button onClick={onClose} data-testid="close-edit-panel" className="text-neutral-400 hover:text-white p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Slug (va en el link/QR de la agencia)</label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-neutral-500">/a/</span>
          <input
            value={slugInput}
            onChange={(e) => setSlugInput(e.target.value.toLowerCase())}
            data-testid="edit-agency-slug"
            className="flex-1 min-w-[160px] bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-3 py-2 text-sm text-white font-mono"
          />
          <button
            onClick={renameSlug}
            disabled={savingSlug || slugInput.trim().toLowerCase() === agency.slug}
            data-testid="save-agency-slug"
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-xs uppercase tracking-[0.2em] disabled:opacity-40"
          >
            {savingSlug ? "Guardando…" : slugSaved ? "Guardado ✓" : "Cambiar"}
          </button>
        </div>
        {slugErr && <p className="text-xs text-[#FF3B30] mt-1.5">{slugErr}</p>}
        {agency.previous_slugs?.length > 0 && (
          <p className="text-xs text-neutral-600 mt-1.5">Slugs anteriores (siguen funcionando): {agency.previous_slugs.join(", ")}</p>
        )}
      </div>

      {usage && (
        <div className="p-4 rounded-2xl bg-black/40 border border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="agency-usage-summary">
          <span className="text-neutral-400">
            Viajes este mes: <span className="text-white font-mono">{usage.trips_this_month}</span>
            {usage.trips_included != null && (
              <span className="text-neutral-500"> / {usage.trips_included + usage.bonus_trips}</span>
            )}
            {usage.trips_included == null && <span className="text-neutral-500"> (ilimitado)</span>}
          </span>
          {usage.bonus_trips > 0 && (
            <span className="text-[var(--brand-cyan,#FF85AC)]">+{usage.bonus_trips} viajes por código</span>
          )}
          {usage.price_mxn != null && (
            <span className="text-neutral-400">${usage.price_mxn.toLocaleString()} MXN/mes</span>
          )}
          <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
            usage.subscription_status === "active"
              ? "text-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]/30"
              : "text-neutral-500 border-white/10"
          }`}>
            {usage.subscription_status ? `Stripe: ${usage.subscription_status}` : "Sin suscripción Stripe"}
          </span>
        </div>
      )}

      {/* Identity + colors */}
      <div className="grid sm:grid-cols-2 gap-4">
        <input value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
          placeholder="Nombre de la agencia" data-testid="edit-agency-name"
          className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
        <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
          placeholder="Tagline" data-testid="edit-agency-tagline"
          className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
        <div className="flex items-center gap-3">
          <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            data-testid="edit-agency-primary" className="w-10 h-10 shrink-0 rounded-lg bg-transparent border border-white/15 cursor-pointer" />
          <input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            className="flex-1 min-w-0 font-mono text-xs bg-black/40 border border-white/15 outline-none rounded-xl px-3 py-2.5 text-white" />
        </div>
        <div className="flex items-center gap-3">
          <input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
            data-testid="edit-agency-secondary" className="w-10 h-10 shrink-0 rounded-lg bg-transparent border border-white/15 cursor-pointer" />
          <input value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
            className="flex-1 min-w-0 font-mono text-xs bg-black/40 border border-white/15 outline-none rounded-xl px-3 py-2.5 text-white" />
        </div>
        <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          placeholder="Email de contacto" className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
        <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          placeholder="Teléfono" className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
        <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
          placeholder="WhatsApp" className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
        <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder="Sitio web" className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
        <LocationFields form={form} setForm={setForm} idPrefix="edit-agency" />
        <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
          data-testid="edit-agency-plan"
          className="bg-black border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2">
          {Object.entries(PLAN_LABELS).map(([v, label]) => <option key={v} value={v}>Plan {label}</option>)}
        </select>
      </div>

      {err && <p className="text-sm text-[#FF3B30]">{err}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="save-agency-edit-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />)}
          {saved ? "Guardado" : "Guardar cambios"}
        </button>
        <button onClick={toggleActive} disabled={togglingActive} data-testid="toggle-agency-active-btn"
          className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold border disabled:opacity-50 ${
            agency.active ? "border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10" : "border-[var(--brand-cyan,#FF85AC)]/40 text-[var(--brand-cyan,#FF85AC)] hover:bg-[var(--brand-cyan,#FF85AC)]/10"
          }`}>
          {togglingActive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
          {agency.active ? "Desactivar agencia" : "Reactivar agencia"}
        </button>
      </div>

      {/* Admins */}
      <div className="pt-6 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <UserCog className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
          <h3 className="font-display text-lg">Administradores de esta agencia</h3>
        </div>

        {adminErr && <p className="text-sm text-[#FF3B30] mb-3">{adminErr}</p>}

        <div className="space-y-3 mb-5" data-testid="agency-admins-list">
          {agencyAdmins.map((a) => (
            <div key={a.booking_id} className="p-3 rounded-2xl bg-black/40 border border-white/10">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-white text-sm">{a.traveler_name} <span className="text-neutral-500">· {a.booking_id}</span></p>
                  <p className="text-xs text-neutral-500">{a.email || "sin correo"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                    a.active ? "text-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]/30" : "text-neutral-500 border-white/10"
                  }`}>{a.active ? "Activo" : "Deshabilitado"}</span>
                  <button onClick={() => resetAdminPassword(a)} disabled={resettingId === a.booking_id}
                    data-testid={`reset-password-${a.booking_id}`} title="Resetear contraseña"
                    className="text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] p-1.5">
                    {resettingId === a.booking_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  </button>
                  <button onClick={() => toggleAdmin(a)} data-testid={`toggle-agency-admin-${a.booking_id}`} title={a.active ? "Deshabilitar" : "Habilitar"}
                    className="text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] p-1.5">
                    <Power className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {revealedPasswords[a.booking_id] && (
                <GeneratedCredential
                  testId={`revealed-password-${a.booking_id}`}
                  heading={`Acceso para ${a.traveler_name}`}
                  loginUrl={`${window.location.origin}/admin/login`}
                  bookingId={a.booking_id}
                  password={revealedPasswords[a.booking_id]}
                  email={a.email}
                  greetingName={a.traveler_name.split(" ")[0]}
                  productName={agency.agency_name}
                />
              )}
            </div>
          ))}
          {agencyAdmins.length === 0 && <p className="text-sm text-neutral-500">Sin administradores todavía.</p>}
        </div>

        <form onSubmit={addAdmin} className="grid sm:grid-cols-2 gap-3">
          <input value={newAdmin.booking_id} onChange={(e) => setNewAdmin({ ...newAdmin, booking_id: e.target.value })}
            placeholder="ID de operador (ej. AGENCY-002)" required data-testid="new-agency-admin2-id"
            className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
          <input value={newAdmin.last_name} onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
            placeholder="Apellido (dato interno, no es la contraseña)" required data-testid="new-agency-admin2-key"
            className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
          <input value={newAdmin.traveler_name} onChange={(e) => setNewAdmin({ ...newAdmin, traveler_name: e.target.value })}
            placeholder="Nombre a mostrar" required data-testid="new-agency-admin2-name"
            className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
          <input value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
            placeholder="Correo (opcional)" type="email" data-testid="new-agency-admin2-email"
            className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
          <div className="sm:col-span-2">
            <button type="submit" disabled={addingAdmin} data-testid="add-agency-admin-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0] disabled:opacity-50">
              {addingAdmin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Agregar administrador
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MasterAgencies() {
  const navigate = useNavigate();
  const user = auth.current();
  const [agencies, setAgencies] = useState([]);
  const [agenciesTotal, setAgenciesTotal] = useState(0);
  const [loadingMoreAgencies, setLoadingMoreAgencies] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    slug: "", agency_name: "", primary_color: "#7C3AED", secondary_color: "#FF85AC",
    admin_booking_id: "", admin_last_name: "", admin_name: "",
    contact_email: "", contact_phone: "", whatsapp: "",
    state: "", municipality: "", maps_pin: "",
  });
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [copiedSlug, setCopiedSlug] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [newAgencyCredential, setNewAgencyCredential] = useState(null);
  // Igual que en Admin.jsx: el límite vive en un ref porque "cargar más" lo
  // cambia por fuera de React sin necesitar recrear ningún efecto.
  const agenciesLimitRef = useRef(50);

  useEffect(() => {
    if (!user || user.role !== "master") { navigate("/admin/login"); return; }
    loadAdmins();
  }, []);

  // Espera a que la persona deje de escribir antes de pedirle al backend —
  // evita mandar una búsqueda por cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Un término de búsqueda nuevo siempre reinicia la paginación — mostrar
  // "cargar más" con el límite acumulado de la búsqueda anterior no tendría
  // sentido.
  useEffect(() => {
    agenciesLimitRef.current = 50;
    load();
  }, [debouncedSearch]);

  const load = async () => {
    try {
      const { data } = await api.get("/master/agencies", {
        params: { skip: 0, limit: agenciesLimitRef.current, search: debouncedSearch },
      });
      setAgencies(data.items);
      setAgenciesTotal(data.total);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  };

  const loadMoreAgencies = async () => {
    setLoadingMoreAgencies(true);
    agenciesLimitRef.current += 50;
    try {
      const { data } = await api.get("/master/agencies", {
        params: { skip: 0, limit: agenciesLimitRef.current, search: debouncedSearch },
      });
      setAgencies(data.items);
      setAgenciesTotal(data.total);
    } catch {
      agenciesLimitRef.current -= 50;
    } finally {
      setLoadingMoreAgencies(false);
    }
  };

  const loadAdmins = async () => {
    try { const { data } = await api.get("/admin/admins"); setAdmins(data); } catch {}
  };

  const clientLink = (slug) => `${window.location.origin}/a/${slug}`;

  const createAgency = async (e) => {
    e.preventDefault();
    setErr("");
    setNewAgencyCredential(null);
    setCreating(true);
    try {
      const { data } = await api.post("/master/agencies", form);
      setNewAgencyCredential({
        booking_id: form.admin_booking_id,
        password: data.admin_password,
        agency_name: data.agency_name,
      });
      setForm({
        slug: "", agency_name: "", primary_color: "#7C3AED", secondary_color: "#FF85AC",
        admin_booking_id: "", admin_last_name: "", admin_name: "",
        contact_email: "", contact_phone: "", whatsapp: "",
        state: "", municipality: "", maps_pin: "",
      });
      load();
      loadAdmins();
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo crear la agencia");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(clientLink(slug));
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(""), 2000);
    } catch {}
  };

  const downloadQr = async (slug) => {
    try {
      const resp = await fetch(qrUrl(clientLink(slug)));
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `qr-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {}
  };

  // La búsqueda ya la resuelve el backend (junto con la paginación) —
  // `agencies` es exactamente lo que hay que mostrar, sin filtrar de nuevo
  // aquí encima.
  const editingAgency = agencies.find((a) => a.id === editingId);

  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12 space-y-10">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Master · Tulkas</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            Tus <span className="text-[var(--brand-cyan,#FF85AC)]">Agencias</span>
          </h1>
          <p className="text-neutral-400 mt-3 max-w-2xl">
            Cada agencia que contrata el servicio obtiene su propia marca (nombre, logo, color) y
            sus propios datos. Comparte el link o el código QR con cada agencia para que sus
            clientes entren directo a su versión de la app.
          </p>
        </div>

        {editingAgency && (
          <AgencyEditPanel
            agency={editingAgency}
            admins={admins}
            onClose={() => setEditingId(null)}
            onSaved={() => { load(); }}
            onAdminsChanged={() => { loadAdmins(); load(); }}
          />
        )}

        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Dar de alta una agencia</h2>
          </div>
          {err && <p className="text-sm text-[#FF3B30] mb-4">{err}</p>}
          <form onSubmit={createAgency} className="grid sm:grid-cols-2 gap-4">
            <input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
              placeholder="slug (ej. agencia-luna)"
              data-testid="new-agency-slug"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={form.agency_name}
              onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
              placeholder="Nombre de la agencia"
              data-testid="new-agency-name"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                data-testid="new-agency-color"
                title="Color primario"
                className="w-10 h-10 shrink-0 rounded-lg bg-transparent border border-white/15 cursor-pointer"
              />
              <input
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                placeholder="Color primario"
                className="flex-1 min-w-0 font-mono text-xs bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-3 py-2.5 text-white"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.secondary_color}
                onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                data-testid="new-agency-secondary-color"
                title="Color secundario"
                className="w-10 h-10 shrink-0 rounded-lg bg-transparent border border-white/15 cursor-pointer"
              />
              <input
                value={form.secondary_color}
                onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                placeholder="Color secundario"
                className="flex-1 min-w-0 font-mono text-xs bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-3 py-2.5 text-white"
              />
            </div>
            <input
              value={form.admin_booking_id}
              onChange={(e) => setForm({ ...form, admin_booking_id: e.target.value.toUpperCase() })}
              placeholder="ID de operador de la agencia (ej. AGENCY-002)"
              data-testid="new-agency-admin-id"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={form.admin_last_name}
              onChange={(e) => setForm({ ...form, admin_last_name: e.target.value })}
              placeholder="Apellido (dato interno, no es la contraseña)"
              data-testid="new-agency-admin-key"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={form.admin_name}
              onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
              placeholder="Nombre del administrador"
              data-testid="new-agency-admin-name"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2"
            />
            <input
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="Correo de contacto (opcional, para enviarle las credenciales)"
              type="email"
              data-testid="new-agency-contact-email"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              placeholder="WhatsApp de contacto (opcional, ej. 528112345678)"
              data-testid="new-agency-whatsapp"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <LocationFields form={form} setForm={setForm} idPrefix="new-agency" />
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={creating}
                data-testid="create-agency-btn"
                className="bg-[var(--brand-cyan,#FF85AC)] text-black px-6 py-2.5 rounded-full font-semibold hover:bg-[#FF9DC0] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crear agencia
              </button>
            </div>
          </form>

          {newAgencyCredential && (
            <GeneratedCredential
              testId="new-agency-admin-credentials"
              heading={`Acceso creado para ${newAgencyCredential.agency_name}`}
              loginUrl={`${window.location.origin}/admin/login`}
              bookingId={newAgencyCredential.booking_id}
              password={newAgencyCredential.password}
              greetingName={newAgencyCredential.agency_name}
              productName="Travel Shield"
            />
          )}
        </article>

        <div>
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <h2 className="font-display text-xl">
              Agencias <span className="text-neutral-500 text-base">({agenciesTotal})</span>
            </h2>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-neutral-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por agencia o administrador…"
                data-testid="agency-search-input"
                className="w-full bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-full pl-10 pr-4 py-2.5 text-sm text-white"
              />
            </div>
          </div>

          {loadError && (
            <div className="mb-4 p-4 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-between gap-4" data-testid="agencies-load-error">
              <p className="text-sm text-[#FF3B30]">No se pudo cargar la lista de agencias — esto NO significa que no tengas ninguna.</p>
              <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] underline shrink-0">Reintentar</button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="agencies-list">
            {agencies.map((a) => (
              <article key={a.id} className="glass rounded-2xl p-5 border border-white/10 flex flex-col">
                <div className="flex items-start gap-3 mb-3">
                  <img
                    src={qrUrl(clientLink(a.slug), 96)}
                    alt={`QR ${a.agency_name}`}
                    className="w-14 h-14 rounded-lg border border-white/10 bg-white p-1 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: a.primary_color }} />
                      <p className="font-display text-base truncate">{a.agency_name}</p>
                    </div>
                    <p className="text-sm text-neutral-400">/a/{a.slug}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border border-white/10 rounded-full px-2 py-0.5">
                        {PLAN_LABELS[a.plan] || "Starter"}
                      </span>
                      {!a.active && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] border border-[#FF3B30]/30 rounded-full px-2 py-0.5">
                          Desactivada
                        </span>
                      )}
                      {a.active && a.traveler_count === 0 && (
                        <span
                          title="Activa pero sin ningún viajero cargado todavía — vale la pena contactarla"
                          className="text-[10px] uppercase tracking-[0.2em] text-amber-400 border border-amber-400/30 rounded-full px-2 py-0.5"
                        >
                          Sin uso
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {a.admin_names?.length > 0 && (
                  <p className="text-xs text-neutral-600 mb-2 truncate">Admin: {a.admin_names.join(", ")}</p>
                )}

                <div className="flex items-center gap-3 text-sm text-neutral-300 mb-3">
                  <span className="inline-flex items-center gap-1.5">
                    <Users2 className="w-3.5 h-3.5 text-[var(--brand-cyan,#FF85AC)]" /> {a.traveler_count} viajeros
                  </span>
                  {a.active_sos_count > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[#FF3B30]">
                      <ShieldAlert className="w-3.5 h-3.5" /> {a.active_sos_count} SOS
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mt-auto pt-1 flex-wrap">
                  <button
                    onClick={() => setEditingId(a.id)}
                    data-testid={`edit-agency-${a.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] hover:underline"
                  >
                    <Settings2 className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button
                    onClick={() => copyLink(a.slug)}
                    data-testid={`copy-link-${a.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)]"
                  >
                    {copiedSlug === a.slug ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedSlug === a.slug ? "Copiado" : "Copiar"}
                  </button>
                  <button
                    onClick={() => downloadQr(a.slug)}
                    data-testid={`download-qr-${a.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)]"
                  >
                    <Download className="w-3.5 h-3.5" /> QR
                  </button>
                </div>
              </article>
            ))}
            {agenciesTotal === 0 && !loadError && debouncedSearch && (
              <p className="text-sm text-neutral-500">Ninguna agencia coincide con "{debouncedSearch}".</p>
            )}
            {agenciesTotal === 0 && !loadError && !debouncedSearch && (
              <p className="text-sm text-neutral-500">Aún no has dado de alta ninguna agencia.</p>
            )}
          </div>
          {agencies.length < agenciesTotal && (
            <button
              onClick={loadMoreAgencies}
              disabled={loadingMoreAgencies}
              data-testid="load-more-agencies-btn"
              className="w-full mt-4 py-2.5 rounded-full border border-white/15 text-sm text-neutral-400 hover:border-[var(--brand-cyan,#FF85AC)]/40 hover:text-[var(--brand-cyan,#FF85AC)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loadingMoreAgencies && <Loader2 className="w-4 h-4 animate-spin" />}
              Cargar más ({agenciesTotal - agencies.length} restantes)
            </button>
          )}
        </div>
      </section>
    </Layout>
  );
}
